import nbLocale from "@fullcalendar/core/locales/nb";
import FullCalendar from "@fullcalendar/react";
import timeGridPlugin from "@fullcalendar/timegrid";
import { Box, Button, Modal } from "@mantine/core";
import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { useElementSize } from "usehooks-ts";
import { FailedAlert } from "../../../../components";
import McDropdownNew from "../../../../components/McDropdown/MCDropdownNew";
import WidgetWrapper from "../../../../components/WidgetWrapper/WidgetWrapper";
import { addAppModalThunk } from "../../../../global/globalSlice";
import { useAppDispatch, useAppSelector } from "../../../../hooks";
import { AppModalEnums } from "../../../../mc-shared/sharedConstants";
import { useGetMeetingSeriesQuery } from "../../meeting/meetingSerieRTK";
import { setActiveMeetingAppTabInRedux } from "../../meeting/meetingSlice";
import { MEETINGS_SERIES_STATUSES, MeetingSeriesStatusEnums } from "./meetingSeriesConstants";
import { ICalenderEvent } from "./types";
import { convertMeetingSeriesToCalenderEvent } from "./utils";

const MeetingSeries: React.FC = () => {
  const [calenderEvents, setCalenderEvents] = useState<ICalenderEvent[]>();
  const [showFailedAlert, setShowFailedAlert] = useState<boolean>(false);
  const [showSettings, setShowSettings] = useState<boolean>(false);
  const [openCalendarModal, setOpenCalendarModal] = useState<boolean>(false);

  const [activeStatus, setActiveStatus] = useState<MeetingSeriesStatusEnums>(MeetingSeriesStatusEnums.ACTIVE);

  const project = useAppSelector((state) => state.adminReducer.project);

  const openedAppModals = useAppSelector((state) => state.globalReducer.openedAppModals);
  const [containerRef, { height, width }] = useElementSize();

  const history = useHistory();

  const {
    data: meetingSeries,
    isError,
    isSuccess,
  } = useGetMeetingSeriesQuery({
    projectId: project._id,
  });

  useEffect(() => {
    init();
  }, [isSuccess]);

  const dispatch = useAppDispatch();
  useEffect(() => {
    if (openedAppModals.length === 0) {
      init();
    }
  }, [openedAppModals.length]);

  const init = async () => {
    try {
      const _calenderEvents = (meetingSeries || []).reduce((acc, meetinSerie) => {
        const events = convertMeetingSeriesToCalenderEvent(meetinSerie);
        acc.push(...events);
        return acc;
      }, []);
      setCalenderEvents(_calenderEvents);
    } catch (error) {
      setShowFailedAlert(true);
    }
  };

  const onEventClick = (info) => {
    info.jsEvent.preventDefault();
    const meetingSerie = meetingSeries.find((ms) => ms.name === info.event.title);
    dispatch(
      addAppModalThunk({
        app: AppModalEnums.MEETING_SERIE,
        projectId: project._id,
        itemId: meetingSerie._id,
        startIsoString: new Date(info.event.start).toISOString(),
      }),
    );
  };

  const goToMeetingApp = () => {
    dispatch(setActiveMeetingAppTabInRedux("MEETING_SERIES"));
    history.push(`/project/${project._id}/meetings`);
  };

  return (
    <WidgetWrapper
      title="Prosjektkalender"
      widgetKey="meetingSeries"
      onFullscreenClick={() => setOpenCalendarModal(true)}
      onShowSettingsClick={() => setShowSettings(!showSettings)}
    >
      {showFailedAlert === true || (isError && <FailedAlert errorMessage="Failed to load meeting series" />)}
      {calenderEvents != null && showFailedAlert === false && (
        <>
          {showSettings && (
            <div className="d-flex flex-wrap justify-content-end w-100 mb-2">
              <McDropdownNew
                color="mc-blue"
                buttonProps={{ style: { width: "150px" } }}
                options={MEETINGS_SERIES_STATUSES}
                displayKey="label"
                currentValue={MEETINGS_SERIES_STATUSES.find((item) => item.value === activeStatus)}
                onChange={(_, item) => setActiveStatus(item.value as MeetingSeriesStatusEnums)}
                defaultTitle="Filter"
              />
              <Button onClick={goToMeetingApp} className="mc-blue text-white mx-1">
                <i className="fa fa-external-link fa-fw" />
                Gå till møteserier
              </Button>
            </div>
          )}
          <div ref={containerRef} className="grow" style={{ fontSize: ".8em", height: "100%" }}>
            <FullCalendar
              allDaySlot={false}
              locale={nbLocale}
              slotMinTime={"08:00:00"}
              slotMaxTime={"17:00:00"}
              slotDuration={height > 400 ? "00:30:00" : "01:00:00"}
              slotLabelFormat={[
                {
                  hour: "2-digit",
                  minute: "2-digit",
                  hour12: false,
                },
              ]}
              plugins={[timeGridPlugin]}
              initialView="timeGridWeek"
              weekends={false}
              height={"100%"}
              expandRows={true}
              // contentHeight="auto"
              events={calenderEvents.filter((event) => event.status === activeStatus)}
              eventClick={onEventClick}
              headerToolbar={
                width > 500
                  ? {
                      start: "title", // will normally be on the left. if RTL, will be on the right
                      center: "",
                      end: "today prev,next", // will normally be on the right. if RTL, will be on the left
                    }
                  : false
              }
            />
          </div>
          <Modal
            opened={openCalendarModal}
            onClose={() => setOpenCalendarModal(false)}
            title={"Prosjektkalender"}
            size="80vw"
            centered
            overlayProps={{ blur: 2 }}
          >
            <Box style={{ maxHeight: "88vh" }}>
              <FullCalendar
                allDaySlot={false}
                locale={nbLocale}
                slotMinTime={"08:00:00"}
                slotMaxTime={"18:00:00"}
                slotLabelFormat={[
                  {
                    hour: "2-digit",
                    minute: "2-digit",
                    hour12: false,
                  },
                ]}
                plugins={[timeGridPlugin]}
                initialView="timeGridWeek"
                weekends={false}
                height={"86vh"}
                expandRows={true}
                events={calenderEvents.filter((event) => event.status === activeStatus)}
                eventClick={onEventClick}
              />
            </Box>
          </Modal>
        </>
      )}
    </WidgetWrapper>
  );
};

export default MeetingSeries;
